import { constants } from '@/constants/constants.ts';
import { PlaceholderType } from '@/types/enums/PlaceholderType.ts';
import { useDocumentContext } from '@/composables/useDocumentContext.ts';
import { computed, nextTick, type Ref, ref, shallowRef, watch } from 'vue';
import type { Signee, SigneeSigning } from '@/types/Signee.ts';
import type { SigneePolicy } from '@/types/SigneePolicy.ts';
import { SignatureType } from '@/types/enums/SignatureType.ts';
import type { AutographPosition } from '@/types/AutographPosition.ts';
import type {
  Placeholder,
  PlaceholderResize,
  PlaceholderTemplateRef
} from '@/composables/placement/placementTypes.ts';
import {
  buildSigneeUpdatePayload,
  clamp,
  getAutoPlacementPositions,
  getPageInfo,
  getPageNumber,
  getPdfPageFromPoint,
  getPlaceholderDraggableInitialState,
  getPlaceholderUid,
  getPositionValues,
  getResizeValues,
  getTopOffset,
  setValues,
  transformPDFCoordinatesToWebCoordinates,
  transformWebCoordinatesToPDFCoordinates,
  waitForDraggableToSettle
} from '@/composables/placement/usePlacementUtility.ts';
import { useGlobalStore } from '@/stores/global.ts';
import { SignStatus } from '@/types/enums/SignStatus.ts';
import { useRoute } from 'vue-router';
import type { Document } from '@/types/Document.ts';
import type { SigneeGroup } from '@/types/deepsign/SigneeGroup.ts';
import type { UpdateSignee } from '@/types/deepsign/UpdateSignee.ts';
import {
  findGroupSigningForSlot,
  findSigneeGroupForSignee,
  getDefaultSigneeFileId,
  getQuorumSlotPlaceholderId,
  getSigneeGroupSlotIndex,
  isPlaceholderForSignee,
  isSigneeGroupQuorumSet
} from '@/utils/signeeSignings.ts';

export type PlaceholderDropResult =
  | { signee: Signee; payload: UpdateSignee; placeholder: Placeholder }
  | undefined;

export const DEFAULT_PAGE_NUMBER = 1;

export const defaultPosition: AutographPosition = {
  pageNumber: DEFAULT_PAGE_NUMBER,
  x: 0,
  y: 0,
  width: constants.SIGNATURE_SIZE.M.WIDTH,
  height: constants.SIGNATURE_SIZE.M.HEIGHT
};

export const defaultAnnotationPosition: AutographPosition = {
  pageNumber: DEFAULT_PAGE_NUMBER,
  x: 0,
  y: 0,
  width: constants.SEAL_ANNOTATION_SIZE.M.WIDTH,
  height: constants.SEAL_ANNOTATION_SIZE.M.HEIGHT
};

export const defaultResizeDimensions: PlaceholderResize = {
  axis: 'both',
  handles: ['e'],
  minWidth: constants.SIGNATURE_SIZE.XS.WIDTH,
  minHeight: constants.SIGNATURE_SIZE.XS.HEIGHT,
  maxWidth: constants.SIGNATURE_SIZE.L.WIDTH,
  maxHeight: constants.SIGNATURE_SIZE.L.HEIGHT
};

export const defaultAnnotationResizeDimensions: PlaceholderResize = {
  axis: 'x',
  handles: ['e'],
  minWidth: constants.SEAL_ANNOTATION_SIZE.S.WIDTH,
  minHeight: constants.SEAL_ANNOTATION_SIZE.S.HEIGHT,
  maxWidth: constants.SIGNATURE_SIZE.L.WIDTH,
  maxHeight: constants.SIGNATURE_SIZE.L.HEIGHT
};

/**
 * Generate a unique identifier for an envelope placeholder.
 * Combines signeeId, signeeGroupId, fileId, signatureSlot, and placeholder type
 * to ensure uniqueness when a signee has multiple placeholders across files/slots.
 */
export function createPlaceholderUid(
  signeeId: string,
  signeeGroupId: string,
  fileId: string,
  signatureSlot: number,
  placeholderType: PlaceholderType = PlaceholderType.SIGNATURE
): string {
  return `${signeeId}-${signeeGroupId}-${fileId}-${signatureSlot}-${placeholderType}`;
}

/**
 * Build placeholders for envelope mode from signeeGroups and their signings.
 * Returns an array of placeholders with envelope metadata (uid, signeeGroupId, fileId, signatureSlot).
 */
export function buildEnvelopeSigneesForSelectedFile(
  document: Document,
  selectedFileId: string
): Placeholder[] {
  const placeholders: Placeholder[] = [];
  const signeeGroups = document?.signeeGroups || [];

  for (const signeeGroup of signeeGroups) {
    for (const signee of signeeGroup.signees || []) {
      // Find signing entry for the selected file
      const signing = signee.signings?.find(
        (s: SigneeSigning) => s.fileId === selectedFileId
      );

      if (!signing) continue;

      // For each autograph position in the signing, create a placeholder
      (signing.autographPositions || []).forEach(
        (position: AutographPosition, slotIndex: number) => {
          if (!position) return;

          const uid = createPlaceholderUid(
            signee.signeeId,
            signeeGroup.signeeGroupId,
            selectedFileId,
            slotIndex,
            PlaceholderType.SIGNATURE
          );

          const placeholder: Placeholder = {
            id: signee.signeeId,
            uid,
            signatureType: signee.signatureType || SignatureType.SIGNATURE,
            signeeType: signee.signeeType,
            signStatus: signee.signStatus,
            email: signee.email,
            text: '',
            index: 0,
            groupIndex: signeeGroup.signOrder,
            placeHolderType: PlaceholderType.SIGNATURE,
            autographPosition: position,
            position,
            isResizeable: true,
            resize: defaultResizeDimensions,
            policy: signee.policy,
            signingPolicy: signing?.policy,
            isVisible: true,
            isPlaced: true,
            isAutoPlaced: false,
            isDraggable: true,
            signeeGroupId: signeeGroup.signeeGroupId,
            fileId: selectedFileId,
            signatureSlot: slotIndex
          };

          placeholders.push(placeholder);

          // If seal, also add annotation placeholder
          if (signee.signatureType === SignatureType.SEAL) {
            const annotationUid = createPlaceholderUid(
              signee.signeeId,
              signeeGroup.signeeGroupId,
              selectedFileId,
              slotIndex,
              PlaceholderType.Annotation
            );

            placeholders.push({
              ...placeholder,
              uid: annotationUid,
              placeHolderType: PlaceholderType.Annotation,
              resize: defaultAnnotationResizeDimensions,
              index: 1
            });
          }
        }
      );
    }
  }

  return placeholders;
}

/**
 * Get the currently selected file ID in envelope mode.
 */
export function getCurrentEnvelopeFileId(
  globalStore: ReturnType<typeof useGlobalStore>
): string {
  return globalStore.selectedDocumentFileId || '';
}

/**
 * Get the next available signature slot index for a signee in a file.
 */
export function getNextEnvelopeSignatureSlot(
  signee: Signee,
  fileId: string
): number {
  if (!signee.signings) {
    return 0;
  }

  const signing = signee.signings.find(
    (s: SigneeSigning) => s.fileId === fileId
  );
  if (!signing) {
    return 0;
  }

  return signing.autographPositions?.length || 0;
}

export const UNGROUPED_SIGNEE_GROUP_ID = 'ungrouped';

export function usePlaceholders() {
  const globalStore = useGlobalStore();
  const route = useRoute();
  const { currentSignees, currentDocument } = useDocumentContext();
  const currentPlaceholders = ref<Placeholder[]>([]);

  const placeholderTemplateRefs = ref<Ref<PlaceholderTemplateRef[]>>(
    shallowRef([])
  );

  const placeholderSetupComplete = ref(false);

  // Per-uid remount counters. The draggable library wires up drag/resize
  // handling against the element captured on mount; a placeholder that is
  // auto-placed while still invisible/zero-sized never gets a working resize
  // subsystem until it remounts. Callers that place placeholders outside the
  // normal render flow (e.g. auto-placing a freshly added signee from the
  // signing-workflow dialog) request a remount for ONLY the affected uids, so
  // already-placed placeholders keep their Draggable (and stay put) instead of
  // all placeholders remounting and re-anchoring.
  const placeholderRemountNonces = ref<Record<string, number>>({});
  function requestPlaceholderRemount(uids?: string[]) {
    if (!uids || uids.length === 0) return;
    const next = { ...placeholderRemountNonces.value };
    for (const uid of uids) {
      next[uid] = (next[uid] ?? 0) + 1;
    }
    placeholderRemountNonces.value = next;
  }

  // Computed ref for envelope mode detection
  const isEnvelopeMode = computed(
    () => (currentDocument.value?.files?.length ?? 0) > 1
  );

  // The file a signee currently places signatures on. In envelope mode this is
  // the selected file; otherwise it is the document's single file. Falls back
  // to the documentId so a stable, unique placeholder uid can always be built.
  function getCurrentDocumentFileId(): string {
    return (
      globalStore.selectedDocumentFileId ||
      getDefaultSigneeFileId(currentDocument.value) ||
      currentDocument.value?.documentId ||
      'document'
    );
  }

  function getSigneeGroupScopeId(signee: Signee): string {
    return signee.signeeGroupId || UNGROUPED_SIGNEE_GROUP_ID;
  }

  // The signee's signings. `signeesOrdered` (the source of `filteredSignees`)
  // does not always carry `signings` — the authoritative multi-placeholder data
  // lives on `document.signeeGroups[].signees[]`. Resolve from there when the
  // signee object itself lacks them so every autograph position is rendered.
  function resolveSigneeSignings(signee: Signee): SigneeSigning[] {
    if (signee.signings?.length) {
      return signee.signings;
    }
    const groups = (currentDocument.value as Document | undefined)
      ?.signeeGroups;
    if (groups) {
      for (const group of groups) {
        const match = group.signees?.find(
          (candidate) => candidate.signeeId === signee.signeeId
        );
        if (match?.signings?.length) {
          return match.signings;
        }
      }
    }
    return signee.signings ?? [];
  }

  // The fileId a signee's placeholders bind to. In envelope mode this is the
  // selected file; in single-file mode we prefer the signee's actual signing
  // fileId (so it matches backend data) and fall back to the resolved document
  // file. Keeps rendering, drag/drop and persistence keyed to one fileId.
  function getSigneeEffectiveFileId(signee: Signee): string {
    const currentFileId = getCurrentDocumentFileId();
    if (isEnvelopeMode.value) {
      return currentFileId;
    }
    const signing = resolveSigneeSignings(signee).find(
      (item) => item.autographPositions?.length
    );
    return signing?.fileId || currentFileId;
  }

  function getPlaceHolderData(invitee: Signee, index: number): Placeholder[] {
    const { policy, signatureType, signeeType, signStatus, email, signeeId } =
      invitee;

    const componentType = getPlaceHolderComponentType(
      signatureType as SignatureType
    );

    const groupScopeId = getSigneeGroupScopeId(invitee);

    // Resolve which file the placeholders belong to and every autograph
    // position on it. In envelope mode we scope strictly to the selected file;
    // in single-file mode we bind to the signee's actual signing file so all of
    // its positions render (and persist to the correct fileId) even when the
    // resolved document fileId differs from the backend's. `signings` is read
    // from the signee (falling back to the document's signeeGroups) so multiple
    // placeholders are produced even when `signeesOrdered` omits them.
    const fileId = getSigneeEffectiveFileId(invitee);

    // Quorum-enabled groups (`requiredSignatures > 0`) own their members'
    // signature slots collectively as a GROUP data structure, not as any
    // member's own placeholder — the slot has no signeeId/email of its own
    // (see `getQuorumSlotPlaceholderId`). So a quorum member never gets a
    // placeholder built for themselves here; every one of the group's shared
    // slots is instead built exactly once, directly from the group, by
    // `getQuorumGroupSlotPlaceholders` (see the rebuild `watch` below).
    const signeeGroup = findSigneeGroupForSignee(
      signeeId,
      (currentDocument.value as Document | undefined)?.signeeGroups
    );
    if (isSigneeGroupQuorumSet(signeeGroup)) {
      return [];
    }

    const signings = resolveSigneeSignings(invitee);
    const signingForFile = signings.find((item) => item.fileId === fileId);

    // Autograph editability is granted per file: the signee-level policy may
    // forbid moving positions while an individual file's signing still allows
    // it (`signing.policy.canModifyAutographPosition`). Build an effective
    // policy for this file so draggability/resizability/visibility reflect the
    // per-file permission rather than only the signee-level one.
    const effectivePolicy: SigneePolicy = {
      ...policy,
      canModifyAutographPosition:
        signingForFile?.policy?.canModifyAutographPosition ??
        policy?.canModifyAutographPosition ??
        false
    };

    // Only the file's own signing positions are rendered in envelope mode. The
    // legacy single `autographPosition` is a document-wide field that a drop
    // populates for the file it was placed on, so falling back to it here would
    // render a phantom placeholder at that position on every other file. Honour
    // the legacy field only in single-file mode where it is the sole source.
    const autographPositions = signingForFile?.autographPositions?.length
      ? signingForFile.autographPositions
      : !isEnvelopeMode.value && invitee.autographPosition
        ? [invitee.autographPosition]
        : [];

    // A signee can own multiple placeholders for a file. When none are placed
    // yet we still render a single empty placeholder so it can be auto-placed,
    // dragged or dropped.
    const slots: Array<AutographPosition | null> =
      autographPositions.length > 0 ? autographPositions : [null];

    return slots.flatMap((autographPosition, slotIndex) => {
      const isDraggable = getPlaceholderDraggableInitialState(
        {
          id: signeeId,
          policy: effectivePolicy,
          autographPosition,
          signatureType: signatureType as SignatureType,
          placeHolderType: componentType,
          isQuorumSlot: false
        },
        route,
        currentDocument.value
      );

      const isPlaced = Boolean(
        (signatureType === SignatureType.SEAL && autographPosition) ||
        (signatureType === SignatureType.SIGNATURE && autographPosition)
      );

      const placeHolder: Placeholder = {
        id: signeeId,
        uid: createPlaceholderUid(
          signeeId,
          groupScopeId,
          fileId,
          slotIndex,
          componentType
        ),
        signatureType: (signatureType ||
          SignatureType.SIGNATURE) as SignatureType,
        signeeType,
        signStatus,
        email,
        text: '',
        index,
        groupIndex: invitee.signOrder,
        placeHolderType: componentType,
        autographPosition: autographPosition || null,
        position: defaultPosition,
        isResizeable: isDraggable || false,
        resize: defaultResizeDimensions,
        policy: effectivePolicy,
        signingPolicy: signingForFile?.policy,
        isVisible: false,
        isPlaced,
        isAutoPlaced: isPlaced,
        isDraggable,
        signeeGroupId: invitee.signeeGroupId,
        fileId,
        signatureSlot: slotIndex,
        isQuorumSlot: false
      };

      if (invitee.signatureType === SignatureType.SEAL) {
        const annotationPlaceholder: Placeholder = {
          ...placeHolder,
          uid: createPlaceholderUid(
            signeeId,
            groupScopeId,
            fileId,
            slotIndex,
            PlaceholderType.Annotation
          ),
          index: index + 1,
          placeHolderType: PlaceholderType.Annotation,
          autographPosition: null,
          position: defaultAnnotationPosition,
          resize: defaultAnnotationResizeDimensions,
          isResizeable: true,
          isVisible: false,
          isPlaced: false,
          isAutoPlaced: false,
          isDraggable: true
        };
        return [placeHolder, annotationPlaceholder];
      }

      return [placeHolder];
    });
  }

  function getPlaceHolderComponentType(type: SignatureType) {
    switch (type) {
      case SignatureType.SIGNATURE:
        return PlaceholderType.SIGNATURE;
      case SignatureType.SEAL:
        return PlaceholderType.SEAL;
      default:
        return PlaceholderType.SIGNATURE;
    }
  }

  /**
   * Builds every shared signature slot for a quorum-enabled signee group
   * (`requiredSignatures > 0`) on the given file — exactly `requiredSignatures`
   * of them, generated once per group rather than once per member. A slot is a
   * data structure OWNED BY THE GROUP: its `id` is a synthetic, group-derived
   * value (`getQuorumSlotPlaceholderId`), and unlike an ordinary signee's
   * placeholder it never carries an `email` — the slot isn't any one
   * signee's own placeholder, so no single member's identity belongs on it.
   * `signatureType`/`signeeType`/`policy`/`signStatus` are read off the
   * group's first member since a quorum group's members always share the
   * same signing method; which particular member is otherwise irrelevant to
   * a slot that belongs to the group as a whole.
   */
  function getQuorumGroupSlotPlaceholders(
    signeeGroup: SigneeGroup,
    index: number
  ): Placeholder[] {
    const representative = signeeGroup.signees?.[0];
    if (!representative) return [];

    const fileId = getSigneeEffectiveFileId(representative);
    const { policy, signatureType, signeeType, signStatus } = representative;
    const componentType = getPlaceHolderComponentType(
      signatureType as SignatureType
    );
    const requiredSignatures = signeeGroup.requiredSignatures ?? 0;

    return Array.from({ length: requiredSignatures }, (_, signatureSlot) => {
      const groupSigning = findGroupSigningForSlot(
        signeeGroup,
        fileId,
        signatureSlot
      );

      // Once any member of the group actually signs the shared slot, the
      // backend permanently merges that signature's visual into the PDF at
      // this slot's position (`signedSigneeId` is set). Stop rendering the
      // interactive placeholder overlay for it — re-showing an editable box
      // over an already-baked-in signature would be misleading and would
      // still (harmlessly, but pointlessly) accept drags/resizes that no
      // longer do anything.
      if (groupSigning?.signedSigneeId) {
        return [];
      }

      const autographPosition = groupSigning?.autographPositions?.[0] ?? null;
      const isDraggable = getPlaceholderDraggableInitialState(
        {
          policy,
          autographPosition,
          signatureType: signatureType as SignatureType,
          placeHolderType: componentType,
          isQuorumSlot: true
        },
        route,
        currentDocument.value
      );
      const isPlaced = Boolean(autographPosition);
      const id = getQuorumSlotPlaceholderId(
        signeeGroup.signeeGroupId,
        signatureSlot
      );

      const placeHolder: Placeholder = {
        id,
        uid: createPlaceholderUid(
          id,
          signeeGroup.signeeGroupId,
          fileId,
          signatureSlot,
          componentType
        ),
        signatureType: (signatureType ||
          SignatureType.SIGNATURE) as SignatureType,
        signeeType,
        signStatus: signStatus ?? SignStatus.PENDING,
        text: '',
        index: index + signatureSlot,
        groupIndex: signeeGroup.signOrder,
        placeHolderType: componentType,
        autographPosition,
        position: defaultPosition,
        isResizeable: isDraggable || false,
        resize: defaultResizeDimensions,
        policy,
        isVisible: false,
        isPlaced,
        isAutoPlaced: isPlaced,
        isDraggable,
        signeeGroupId: signeeGroup.signeeGroupId,
        fileId,
        signatureSlot,
        isQuorumSlot: true
      };

      if (signatureType === SignatureType.SEAL) {
        const annotationPlaceholder: Placeholder = {
          ...placeHolder,
          uid: createPlaceholderUid(
            id,
            signeeGroup.signeeGroupId,
            fileId,
            signatureSlot,
            PlaceholderType.Annotation
          ),
          index: placeHolder.index + 1,
          placeHolderType: PlaceholderType.Annotation,
          autographPosition: null,
          position: defaultAnnotationPosition,
          resize: defaultAnnotationResizeDimensions,
          isResizeable: true,
          isVisible: false,
          isPlaced: false,
          isAutoPlaced: false,
          isDraggable: true
        };
        return [placeHolder, annotationPlaceholder];
      }

      return [placeHolder];
    }).flat();
  }

  watch(
    (): [
      Signee[] | null | undefined,
      string | null | undefined,
      SigneeGroup[] | undefined
    ] => [
      currentSignees.value,
      globalStore.selectedDocumentFileId,
      (currentDocument.value as Document | undefined)?.signeeGroups
    ],
    async ([newCurrentSignees]) => {
      if (!newCurrentSignees) {
        currentPlaceholders.value = [];
        return;
      }

      const signeesToCheck =
        newCurrentSignees.filter(
          (signee) => signee.signStatus !== SignStatus.SIGNED
        ) || [];

      const memberPlaceholders = signeesToCheck
        .flatMap((invitee: Signee, index: number) =>
          getPlaceHolderData(invitee, index)
        )
        .filter((placeholder): placeholder is Placeholder =>
          Boolean(placeholder)
        );

      // Quorum group slots are group-owned, not member-owned (see
      // `getQuorumGroupSlotPlaceholders`), so they are built once per group
      // here — independently of `signeesToCheck` — rather than folded into
      // the per-signee flatMap above.
      const quorumGroups = (
        (currentDocument.value as Document | undefined)?.signeeGroups ?? []
      ).filter(isSigneeGroupQuorumSet);

      const groupSlotPlaceholders = quorumGroups.flatMap((signeeGroup, index) =>
        getQuorumGroupSlotPlaceholders(
          signeeGroup,
          memberPlaceholders.length + index
        )
      );

      const rebuiltPlaceholders = [
        ...memberPlaceholders,
        ...groupSlotPlaceholders
      ];

      // Preserve in-progress placement state (position, visibility, drag flags)
      // for placeholders that still exist, keyed by their uid. A placeholder's
      // uid embeds the signee's group scope, which is UNSTABLE for a freshly
      // added signee: it is `ungrouped` until the server assigns a signeeGroupId
      // and can flip afterwards. So also key by a group-agnostic stable identity
      // (signeeId + fileId + slot + type) and fall back to it, otherwise a
      // just-placed (visible) slot would be dropped and replaced by an invisible
      // rebuilt-from-signings slot when the group scope changes.
      const stableKey = (placeholder: Placeholder): string =>
        [
          placeholder.id || 'placeholder',
          placeholder.fileId || 'document',
          placeholder.signatureSlot ?? 0,
          placeholder.placeHolderType || PlaceholderType.SIGNATURE
        ].join('-');

      // A signee's `signatureSlot` is just the placeholder's local index
      // within its file's `autographPositions` array (see `getPlaceHolderData`
      // / `buildEnvelopeSigneesForSelectedFile`). Removing one of several
      // placed placeholders for the same signee shrinks that array, so every
      // LATER placeholder's slot index shifts down by one on the next rebuild
      // — e.g. deleting slot 1 turns old slot 2 into new slot 1 and old slot 3
      // into new slot 2. Both `uid` and `stableKey` are keyed by that slot
      // index, so they'd wrongly match the NEW slot 2 (which now holds the
      // position that used to be at slot 3) against the OLD slot 2 entry
      // (whose retained `position`/`autographPosition` still describe the
      // OLD slot 2's location) — visually duplicating a stale placeholder atop
      // an unrelated one while silently dropping the true last placeholder.
      // The actual `autographPosition` VALUE is untouched by a sibling's
      // removal, so key by that (when present) and prefer it over the
      // slot-index-based keys, which only apply to not-yet-placed stubs.
      const positionKey = (placeholder: Placeholder): string | null => {
        const position = placeholder.autographPosition;
        if (!position) return null;
        return [
          placeholder.id || 'placeholder',
          placeholder.fileId || 'document',
          placeholder.placeHolderType || PlaceholderType.SIGNATURE,
          position.pageNumber,
          Math.round(position.x),
          Math.round(position.y),
          Math.round(position.width),
          Math.round(position.height)
        ].join('-');
      };

      // Group scope used to mint slots for genuinely new placeholders without
      // colliding with a sticky (position-matched) placeholder that kept an
      // old slot number the rebuilt data no longer assigns to it (see below).
      const groupKey = (placeholder: Placeholder): string =>
        [
          placeholder.id || 'placeholder',
          placeholder.fileId || 'document',
          placeholder.placeHolderType || PlaceholderType.SIGNATURE
        ].join('-');

      const existingByUid = new Map<string, Placeholder>();
      const existingByStableKey = new Map<string, Placeholder>();
      const existingByPosition = new Map<string, Placeholder>();
      const usedSlotsByGroup = new Map<string, Set<number>>();
      for (const placeholder of currentPlaceholders.value) {
        existingByUid.set(getPlaceholderUid(placeholder), placeholder);
        existingByStableKey.set(stableKey(placeholder), placeholder);
        const key = positionKey(placeholder);
        if (key) existingByPosition.set(key, placeholder);
        const group = groupKey(placeholder);
        const slots = usedSlotsByGroup.get(group) ?? new Set<number>();
        slots.add(placeholder.signatureSlot ?? 0);
        usedSlotsByGroup.set(group, slots);
      }

      const nextFreeSlot = (group: string): number => {
        const slots = usedSlotsByGroup.get(group) ?? new Set<number>();
        let slot = 0;
        while (slots.has(slot)) slot += 1;
        slots.add(slot);
        usedSlotsByGroup.set(group, slots);
        return slot;
      };

      currentPlaceholders.value = rebuiltPlaceholders.map(
        (placeholder: Placeholder, index) => {
          const rebuiltPositionKey = positionKey(placeholder);
          const existing =
            (rebuiltPositionKey &&
              existingByPosition.get(rebuiltPositionKey)) ??
            existingByUid.get(getPlaceholderUid(placeholder)) ??
            existingByStableKey.get(stableKey(placeholder));
          if (!existing) {
            // A quorum member's `signatureSlot` (see `getPlaceHolderData`) is
            // its fixed, group-wide index (`getSigneeGroupSlotIndex`) — a
            // stable value shared with the backend's persisted slot and the
            // group's other members' numbering, NOT a locally-reassignable
            // array position. Minting a fresh one via `nextFreeSlot` below
            // (which only tracks slots already seen in THIS signee's own
            // placeholders) would ignore that shared numbering and hand out
            // an arbitrary, colliding-with-nothing-but-wrong slot (e.g. every
            // quorum member's first-ever placeholder reading slot 0) — so
            // keep the rebuilt value verbatim for these instead.
            if (placeholder.isQuorumSlot) {
              return { ...placeholder, index };
            }

            // Genuinely new placeholder — mint a slot that isn't already in
            // use by a sticky (position-matched) sibling in this pass (see
            // `nextFreeSlot`), rather than trusting the rebuilt data's own
            // array-index-derived slot, which can collide with one a
            // surviving sibling kept from before a deletion.
            const group = groupKey(placeholder);
            const slot = nextFreeSlot(group);
            const uid = createPlaceholderUid(
              placeholder.id || '',
              placeholder.signeeGroupId || UNGROUPED_SIGNEE_GROUP_ID,
              placeholder.fileId || '',
              slot,
              placeholder.placeHolderType
            );
            return { ...placeholder, signatureSlot: slot, uid, index };
          }
          // Keep the live placement state but refresh signee-derived metadata.
          // `isDraggable`/`isResizeable` are derived from the (per-file) policy,
          // which can arrive after the placeholder was first built — e.g. a
          // freshly added signee is auto-placed before its signing policy is in
          // local data. Refresh them here (together with `policy`).
          //
          // `isQuorumSlot` always adopts the freshly rebuilt value — it's
          // recomputed every pass straight from the CURRENT `signeeGroups`
          // (see `getPlaceHolderData`), so it's always authoritative, unlike
          // `existing`'s value which can be stale from a transient in-between
          // rebuild (e.g. right after a group member is removed, the quorum
          // briefly reads as unset before it's restored/reclamped — see
          // `reconcileGroupQuorums` — producing an intermediate non-quorum
          // placeholder that a later pass's uid/stableKey can coincidentally
          // still match). Blindly keeping a stale `existing.isQuorumSlot` here
          // would then permanently show the member's email instead of their
          // "Slot N" label even once the real quorum data arrives.
          //
          // For the SAME reason, `signatureSlot` adopts the rebuilt value too
          // when the placeholder IS a quorum slot: a quorum member's slot is
          // its fixed, group-wide index (`getSigneeGroupSlotIndex`), which can
          // legitimately change when group membership changes and must be
          // adopted immediately — unlike a non-quorum signee's own local
          // array-index slot, which a sibling's removal shifts on rebuild even
          // though its actual placed position is untouched (that's what
          // `positionKey` matched on above), so it deliberately keeps
          // `existing.signatureSlot` instead.
          const isQuorumSlot = placeholder.isQuorumSlot;
          const signatureSlot = isQuorumSlot
            ? placeholder.signatureSlot
            : existing.signatureSlot;
          const uid = createPlaceholderUid(
            existing.id || placeholder.id || '',
            placeholder.signeeGroupId || UNGROUPED_SIGNEE_GROUP_ID,
            placeholder.fileId || existing.fileId || '',
            signatureSlot ?? 0,
            placeholder.placeHolderType
          );
          return {
            ...existing,
            uid,
            signeeGroupId: placeholder.signeeGroupId,
            email: placeholder.email,
            signStatus: placeholder.signStatus,
            signatureType: placeholder.signatureType,
            signeeType: placeholder.signeeType,
            policy: placeholder.policy,
            isDraggable: placeholder.isDraggable,
            isResizeable: placeholder.isResizeable,
            // Prefer the freshly rebuilt value (derived from the latest
            // persisted signee data) over the possibly-stale one already on
            // `existing` — e.g. right after a drag/resize is saved, the
            // rebuilt value reflects the new position while `existing` still
            // holds the pre-update one. Keeping the stale value here would
            // make a later autograph-driven re-placement (e.g. after adding
            // or removing an envelope file) snap the placeholder back to its
            // previous position/size.
            autographPosition:
              placeholder.autographPosition ?? existing.autographPosition,
            groupIndex: placeholder.groupIndex,
            signatureSlot,
            isQuorumSlot,
            index
          };
        }
      );

      // Self-heal visibility: a placeholder reconstructed from persisted
      // `signings` is built invisible at the default position (like right after
      // a page load). PlaceholderWrapper only makes those visible on mount / PDF
      // render / file switch — none of which fire when placeholders are rebuilt
      // in-place after a signee update (e.g. auto-placing a dialog-added signee
      // or an InviteeCard click). So once the PDF is set up, re-apply the
      // autograph-driven placement here for any placeholder that carries an
      // autographPosition but ended up invisible, so it renders at its persisted
      // position without a manual reload. Already-visible placeholders are left
      // untouched to avoid snapping ones the user may be interacting with.
      if (placeholderSetupComplete.value) {
        await nextTick();
        placePlaceholdersByAutographData({ onlyInvisible: true });
      }
    },
    { immediate: true, deep: true }
  );

  async function resetPlaceholders() {
    const resetPromises: Promise<void>[] = [];

    for (const placeholder of currentPlaceholders.value) {
      const positionDefault =
        placeholder.placeHolderType === PlaceholderType.Annotation
          ? defaultAnnotationPosition
          : defaultPosition;

      const resizeDefault =
        placeholder.placeHolderType === PlaceholderType.Annotation
          ? defaultAnnotationResizeDimensions
          : defaultResizeDimensions;

      if (!placeholder.isDraggable) continue;

      resetPromises.push(
        resetPlaceholderState(placeholder, positionDefault, resizeDefault)
      );
    }

    return Promise.all(resetPromises);
  }

  async function resetPlaceholderState(
    placeholderIdentifier: Partial<Placeholder>,
    positionDefault: AutographPosition = defaultPosition,
    resizeDefault: PlaceholderResize = defaultResizeDimensions
  ) {
    const placeholder = currentPlaceholders.value.find(
      (ph) => getPlaceholderUid(ph) === getPlaceholderUid(placeholderIdentifier)
    );
    if (!placeholder) return;
    const position = await getPositionValues(
      positionDefault,
      placeholder.position?.pageNumber
    );
    const resize = await getResizeValues(
      resizeDefault,
      placeholder.position?.pageNumber
    );

    const isDraggable = getPlaceholderDraggableInitialState(
      placeholder,
      route,
      currentDocument.value
    );

    setValues(placeholder, [
      { resize },
      { position },
      { text: '' },
      { isVisible: false },
      { isPlaced: false },
      { isDraggable },
      { isAutoPlaced: false },
      { isResized: false },
      { autographPosition: null }
    ]);
    // Assigned directly since `pdfPosition` may not already be a key on the
    // target (setValues only updates keys already present on the object).
    placeholder.pdfPosition = null;
  }

  function placePlaceholdersByAutographData(options?: {
    onlyInvisible?: boolean;
  }) {
    const onlyInvisible = options?.onlyInvisible ?? false;
    currentPlaceholders.value.forEach(async (placeholder: Placeholder) => {
      // When healing a rebuild, skip placeholders that are already visible so we
      // don't snap ones the user may be dragging/resizing back to their
      // persisted position.
      if (onlyInvisible && placeholder.isVisible) {
        return;
      }

      const isDraggable = getPlaceholderDraggableInitialState(
        placeholder,
        route,
        currentDocument.value
      );

      const isPlaced = Boolean(
        (placeholder?.signatureType === SignatureType.SEAL &&
          placeholder?.autographPosition) ||
        (placeholder?.signatureType === SignatureType.SIGNATURE &&
          placeholder?.autographPosition)
      );

      // Prefer the locally cached `pdfPosition`, refreshed after every manual
      // drag/resize, over the persisted `autographPosition`. `autographPosition`
      // only updates once a placement is saved to the backend, so relying on it
      // alone here would revert an in-progress (not-yet-saved) drag back to its
      // previous position the next time this runs (e.g. on a zoom-triggered
      // resize), which is exactly the "snap back" this guards against.
      const sourcePosition =
        placeholder.pdfPosition ?? placeholder.autographPosition;

      const position =
        sourcePosition &&
        (placeholder.signatureType === SignatureType.SEAL ||
          (sourcePosition &&
            placeholder.signatureType === SignatureType.SIGNATURE))
          ? await transformPDFCoordinatesToWebCoordinates(
              sourcePosition as AutographPosition
            )
          : placeholder.placeHolderType === PlaceholderType.Annotation
            ? defaultAnnotationPosition
            : defaultPosition;

      const resize = await getResizeValues(
        defaultResizeDimensions,
        globalStore.currentPage || placeholder.position?.pageNumber
      );

      if (placeholder.autographPosition) {
        setValues(placeholder, [
          { isAutoPlaced: isPlaced },
          { isVisible: isPlaced },
          { isPlaced },
          { isDraggable },
          { position },
          { resize }
        ]);
        return;
      }

      // No persisted `autographPosition` yet, but we still have the
      // scale-independent `pdfPosition` cached from the last manual
      // drag/resize. Re-anchor from it so an as-yet-unsaved placeholder
      // doesn't end up with stale pixel coordinates once the page's
      // on-screen scale changes, without touching its visibility/placed
      // state.
      if (placeholder.isVisible && placeholder.pdfPosition) {
        setValues(placeholder, [{ isDraggable }, { position }]);
      }
    });
  }

  async function placePlaceholdersAutomatically(
    signeesToPlace = null,
    placeholdersToPlace: Placeholder[] | null = null
  ) {
    await nextTick();
    const signeeGroups = (currentDocument.value as Document | undefined)
      ?.signeeGroups;
    const signeesToProcess: Placeholder[] | null = placeholdersToPlace
      ? placeholdersToPlace
      : signeesToPlace
        ? (signeesToPlace as Signee[]).flatMap((signee) =>
            currentPlaceholders.value.filter(
              (placeholder) =>
                isPlaceholderForSignee(
                  placeholder,
                  signee.signeeId,
                  signeeGroups
                ) && placeholder.placeHolderType !== PlaceholderType.Annotation
            )
          )
        : null;

    const hiddenPositions =
      signeesToProcess ||
      currentPlaceholders.value.filter((placeholder: Placeholder) => {
        return !placeholder.isVisible;
      });
    const visiblePlaceholders = currentPlaceholders.value.filter(
      (placeholder: Placeholder) => placeholder.isVisible
    );

    for (const placeholder of hiddenPositions) {
      // `placeholder.position` starts out as the shared `defaultPosition`
      // object, whose `pageNumber` is always `DEFAULT_PAGE_NUMBER` (1) — a
      // truthy placeholder value, not a real position. Falling back to it
      // (as `placeholder.position?.pageNumber || globalStore.currentPage`
      // used to) meant a brand-new placeholder always landed on page 1 of
      // the active file instead of whichever page the user is viewing. Only
      // an already-placed placeholder has a meaningful page to keep; every
      // other case should use the current page.
      const targetPageNumber = placeholder.isPlaced
        ? (placeholder.position?.pageNumber ?? globalStore.currentPage)
        : globalStore.currentPage;
      placeholder.position = await getPositionValues(
        defaultPosition,
        targetPageNumber
      );
      placeholder.resize = await getResizeValues(
        defaultResizeDimensions,
        targetPageNumber
      );
    }

    await getAutoPlacementPositions(
      globalStore.currentPage,
      hiddenPositions,
      visiblePlaceholders,
      placeholderTemplateRefs
    );

    for (const placeholder of hiddenPositions) {
      placeholder.isAutoPlaced = true;
      placeholder.isPlaced = true;
      placeholder.isVisible = true;
    }
    return hiddenPositions;
  }

  // Slot-lifecycle helper shared by every "add a signature placeholder for a
  // signee" flow (drag-and-drop and the InviteeCard place click). It resolves
  // the signee's effective file, then either reuses an existing not-yet-placed
  // slot (e.g. the initial empty placeholder on the first placement) or mints a
  // fresh slot (new signatureSlot + uid) appended to currentPlaceholders. It
  // owns placeholder identity only — positioning/visibility is the caller's
  // concern (drop uses cursor coordinates; the click uses
  // placePlaceholdersAutomatically). The returned slot is left invisible so it
  // is excluded from auto-placement's occupied set until the caller places it.
  function createOrReuseSignatureSlot(
    signee: Signee
  ): { placeholder: Placeholder; fileId: string } | undefined {
    const signeeIndex =
      currentSignees.value?.findIndex(
        (candidate) => candidate.signeeId === signee.signeeId
      ) ?? -1;
    const match =
      signeeIndex >= 0 ? currentSignees.value?.[signeeIndex] : signee;
    if (!match) return undefined;

    const fileId = getSigneeEffectiveFileId(match);
    const groupScopeId = getSigneeGroupScopeId(match);
    const signeeGroup = findSigneeGroupForSignee(
      match.signeeId,
      (currentDocument.value as Document | undefined)?.signeeGroups
    );
    const isQuorumMember = isSigneeGroupQuorumSet(signeeGroup);

    // A quorum member's own shared slot is a GROUP-OWNED placeholder,
    // pre-generated once per group by `getQuorumGroupSlotPlaceholders` (see
    // the rebuild watch) — it always already exists in `currentPlaceholders`,
    // keyed by the member's own fixed ordinal (`getSigneeGroupSlotIndex`)
    // rather than by their signeeId. There is never a "mint a new slot" case
    // for a quorum member: only reuse/re-surface that one shared slot.
    if (isQuorumMember && signeeGroup) {
      const ownSlot = getSigneeGroupSlotIndex(signeeGroup, match.signeeId);
      const groupPlaceholder = currentPlaceholders.value.find(
        (placeholder) =>
          placeholder.signeeGroupId === signeeGroup.signeeGroupId &&
          placeholder.signatureSlot === ownSlot &&
          placeholder.fileId === fileId &&
          placeholder.placeHolderType !== PlaceholderType.Annotation
      );
      return groupPlaceholder
        ? { placeholder: groupPlaceholder, fileId }
        : undefined;
    }

    const signaturePlaceholders = currentPlaceholders.value.filter(
      (placeholder) =>
        placeholder.id === match.signeeId &&
        placeholder.fileId === fileId &&
        placeholder.placeHolderType !== PlaceholderType.Annotation
    );

    const reused = signaturePlaceholders.find(
      (placeholder) => !placeholder.isVisible
    );
    if (reused) return { placeholder: reused, fileId };

    const template =
      signaturePlaceholders[0] ||
      getPlaceHolderData(match, signeeIndex >= 0 ? signeeIndex : 0).find(
        (placeholder) =>
          placeholder.placeHolderType !== PlaceholderType.Annotation
      );
    if (!template) return undefined;

    const nextSlot =
      signaturePlaceholders.reduce(
        (max, placeholder) =>
          Math.max(max, (placeholder.signatureSlot ?? 0) + 1),
        0
      ) || signaturePlaceholders.length;

    const placeholder: Placeholder = {
      ...template,
      // Deep-copy position/resize: `template` may be an already-placed sibling
      // slot (e.g. the signee's first placeholder), and a shallow spread would
      // leave this new slot's `position`/`resize` pointing at the SAME object
      // as the template's. Until `placePlaceholdersAutomatically` reassigns
      // this slot's own position, the draggable library could mount/anchor it
      // against that shared object, causing the persisted payload to
      // (transiently) read the template's coordinates for this slot too.
      position: { ...(template.position ?? defaultPosition) },
      resize: { ...template.resize },
      uid: createPlaceholderUid(
        match.signeeId,
        groupScopeId,
        fileId,
        nextSlot,
        template.placeHolderType
      ),
      fileId,
      signatureSlot: nextSlot,
      autographPosition: null,
      isVisible: false
    };
    currentPlaceholders.value.push(placeholder);
    return { placeholder, fileId };
  }

  async function movePlaceHolders(
    fromPage: number,
    toPage: number,
    placeholdersToMove: Placeholder[]
  ) {
    const toPageDetails = await getPageInfo(toPage);

    // Precompute target page information
    const toTopOffset = getTopOffset(
      toPageDetails.currentPageNode as HTMLElement
    );
    const toWidth = Math.floor(toPageDetails.webPageDetails.width);
    const toHeight = Math.floor(toPageDetails.webPageDetails.height);
    let isAnyOutOfBounds = false;
    try {
      for (const placeholder of placeholdersToMove) {
        if (!placeholder.position) continue;

        // Determine the actual source page for this placeholder
        const sourcePageNumber =
          placeholder.position.pageNumber || fromPage || 1;

        const fromPageDetails = await getPageInfo(sourcePageNumber);
        const fromWidth = Math.floor(fromPageDetails.webPageDetails.width);
        const fromHeight = Math.floor(fromPageDetails.webPageDetails.height);

        const isOutOfBounds = fromWidth > toWidth || fromHeight > toHeight;

        if (isOutOfBounds) {
          // If any page differs in size and thus placeholders might end up being out of bounds relative to the target page break the loop
          isAnyOutOfBounds = true;
          break;
        }

        const fromTopOffset = getTopOffset(
          fromPageDetails.currentPageNode as HTMLElement
        );

        // Compute the move purely from `placeholder.position` — the app's
        // own authoritative, scale-correct source of truth (kept in sync by
        // drag/resize/zoom elsewhere) — rather than reading the placeholder's
        // live DOM/drag context via `placeholderTemplateRefs`. Adding/removing
        // an envelope file re-fetches `document.signeeGroups` (see
        // `refreshDocumentFiles`), which can assign/change a signee's
        // `signeeGroupId` for the first time — and that changes the
        // placeholder's `uid` (see `createPlaceholderUid`), forcing Vue to
        // remount its Draggable component. A freshly remounted Draggable's
        // context/DOM element hasn't necessarily finished (re-)initializing
        // its `x`/style yet, so reading it here (as this used to, via
        // `placeholderTemplateRefs.value[placeholder.index]` — itself also
        // unreliable, since `.index` is the signee's positional index, not
        // its position in that array) could silently produce a stale value
        // and corrupt the moved placeholder's coordinates. `position` itself
        // is preserved intact across that rebuild (see the
        // `existingByStableKey` merge in the placeholder-rebuild watch
        // below), so relying on it is both simpler and immune to this race.
        const currentX = placeholder.position.x || 0;
        const relativeYToOrigin = Math.abs(
          (placeholder.position.y || 0) - fromTopOffset
        );

        // X stays the same, relative to page; Y is recomputed relative to the target page
        placeholder.position.pageNumber = toPage;
        placeholder.position.x = currentX;
        placeholder.position.y = toTopOffset + relativeYToOrigin;
      }
    } catch (error) {
      console.error('Failed to move Placeholders see reason: ', error);
    } finally {
      if (placeholdersToMove.length > 0 && isAnyOutOfBounds) {
        await getAutoPlacementPositions(
          toPage,
          placeholdersToMove,
          [],
          placeholderTemplateRefs
        );
      }
    }

    // Refresh the scale-independent `pdfPosition` cache for every moved
    // placeholder now that `position` reflects the target page. Both the
    // in-bounds branch above and `getAutoPlacementPositions`'s auto-layout
    // mutate `position` directly without touching `pdfPosition`, so without
    // this it would keep pointing at the OLD page/coordinates. A subsequent
    // zoom re-anchors placeholders from `pdfPosition` (see
    // `placePlaceholdersByAutographData`), which would then snap the
    // placeholder back to its pre-move page/location.
    for (const placeholder of placeholdersToMove) {
      if (!placeholder.position || placeholder.position.pageNumber !== toPage) {
        continue;
      }
      placeholder.pdfPosition = await transformWebCoordinatesToPDFCoordinates(
        placeholder.position
      );
    }
  }

  function onSignatureDragOver(event: DragEvent) {
    const transfer = event.dataTransfer;
    if (!transfer) return;

    const isInviteeDrag = Array.from(transfer.types).includes(
      'application/x-signee-id'
    );
    if (!isInviteeDrag) return;

    const isValidDropTarget = Boolean(
      getPdfPageFromPoint(event.clientX, event.clientY)
    );

    if (isValidDropTarget) {
      event.preventDefault();
      transfer.dropEffect = 'copy';
      return;
    }
    transfer.dropEffect = 'none';
  }

  async function onSignatureDrop(event: DragEvent) {
    const isValidDropTarget = Boolean(
      getPdfPageFromPoint(event.clientX, event.clientY)
    );
    if (!isValidDropTarget) return undefined;

    return addPlaceholderFromDrop(event);
  }

  async function addPlaceholderFromDrop(
    event: DragEvent
  ): Promise<PlaceholderDropResult> {
    const signeeId = event.dataTransfer?.getData('application/x-signee-id');
    const email = event.dataTransfer?.getData('application/x-email');
    const cursorOffsetXRatioRaw = event.dataTransfer?.getData(
      'application/x-cursor-offset-x-ratio'
    );
    const cursorOffsetYRatioRaw = event.dataTransfer?.getData(
      'application/x-cursor-offset-y-ratio'
    );
    if (!signeeId) return undefined;

    const page = getPdfPageFromPoint(event.clientX, event.clientY);
    const pageContent = document.querySelector(
      '.page-content'
    ) as HTMLElement | null;

    if (!page || !pageContent) return undefined;

    const pageNumber = getPageNumber(page) || globalStore.currentPage || 1;

    const match = currentSignees.value?.find(
      (signee) => signee.signeeId === signeeId
    );
    if (!match) return undefined;

    const slot = createOrReuseSignatureSlot(match);
    if (!slot) return undefined;
    const { placeholder: newPlaceholder, fileId: currentFileId } = slot;

    newPlaceholder.position = { ...defaultPosition };
    newPlaceholder.resize = { ...defaultResizeDimensions };

    const pos = await getPositionValues(newPlaceholder.position, pageNumber);
    const resize = await getResizeValues(defaultResizeDimensions, pageNumber);
    newPlaceholder.position.pageNumber = pageNumber;
    newPlaceholder.resize = resize;
    newPlaceholder.position = pos;

    const cursorOffsetXRatio = clamp(
      Number(cursorOffsetXRatioRaw || 0.5),
      0,
      1
    );
    const cursorOffsetYRatio = clamp(
      Number(cursorOffsetYRatioRaw || 0.5),
      0,
      1
    );

    const pageRect = page.getBoundingClientRect();
    const contentRect = pageContent.getBoundingClientRect();

    const pageLeft = pageRect.left - contentRect.left + pageContent.scrollLeft;

    const pageTop = pageRect.top - contentRect.top + pageContent.scrollTop;

    const dropX =
      event.clientX -
      contentRect.left +
      pageContent.scrollLeft -
      newPlaceholder.position.width * cursorOffsetXRatio;

    const dropY =
      event.clientY -
      contentRect.top +
      pageContent.scrollTop -
      newPlaceholder.position.height * cursorOffsetYRatio;

    const x = clamp(
      dropX,
      pageLeft,
      pageLeft + page.offsetWidth - newPlaceholder.position.width
    );
    const y = clamp(
      dropY,
      pageTop,
      pageTop + page.offsetHeight - newPlaceholder.position.height
    );

    newPlaceholder.position.x = x;
    newPlaceholder.position.y = y;
    newPlaceholder.isVisible = true;
    newPlaceholder.isPlaced = true;
    // A quorum slot is a group-owned data structure with no signee identity
    // of its own (see `getQuorumGroupSlotPlaceholders`) — never stamp the
    // dragging signee's email onto it, even though they are the one who
    // physically performed the drop.
    if (!newPlaceholder.isQuorumSlot) {
      newPlaceholder.email = email;
    }
    // Mark as auto-placed (i.e. not yet manually dragged/resized via the
    // Draggable component) so the persisted payload trusts this freshly
    // computed `position` instead of racing the Draggable library's own
    // async position initialization when reading the DOM. See the
    // `isAutoPlaced` handling in `calculatePlaceholderCoordinates`.
    newPlaceholder.isAutoPlaced = true;

    // Wait for the dropped placeholder's Draggable to render, then force it
    // to remount before reading its DOM coordinates. `createOrReuseSignatureSlot`
    // may have reused the signee's initial placeholder, which was first
    // mounted while invisible/zero-sized (e.g. at page load, before any drop) —
    // the draggable library anchors its bounds/position off that initial,
    // stale mount and never re-derives them on its own. Without remounting
    // here, `buildSigneeUpdatePayload` below would read that stale DOM
    // position instead of the one we just placed.
    await nextTick();
    await waitForDraggableToSettle();
    if (newPlaceholder.uid) {
      requestPlaceholderRemount([newPlaceholder.uid]);
    }
    await nextTick();

    const payload = await buildSigneeUpdatePayload(
      match,
      currentPlaceholders.value,
      placeholderTemplateRefs.value,
      currentDocument.value,
      currentFileId
    );

    return { signee: match, payload, placeholder: newPlaceholder };
  }

  return {
    onSignatureDragOver,
    onSignatureDrop,
    addPlaceholderFromDrop,
    currentPlaceholders,
    placeholderTemplateRefs,
    resetPlaceholderState,
    resetPlaceholders,
    placePlaceholdersByAutographData,
    placePlaceholdersAutomatically,
    createOrReuseSignatureSlot,
    movePlaceHolders,
    placeholderSetupComplete,
    placeholderRemountNonces,
    requestPlaceholderRemount,
    isEnvelopeMode
  };
}
